import _defineProperty from "/build/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { GET_PLAYBACK } from "@/apollo/queries/playback";
import { useAuthUserQuery, useDeviceInfoQuery, usePlaybackSessionCloseMutation, usePlaybackSessionOpenMutation, useChatGetRoomsLazyQuery } from "@/hooks";
import AreaCheckAPI from "@/lib/AreaCheckAPI";
import { rightsControlType, userRating } from "@/lib/constants";
import dayjs from "@/lib/day";
import { AdultNotViewableError, GEOLOCATION_ERROR_CODES, GEOLOCATION_ERROR_MESSAGES, GEOLOCATION_ERROR_MESSAGES_ANDROID, GEOLOCATION_ERROR_MESSAGES_IOS, GeoLocationError, PREPARE_TO_PLAY_OVERLAY_CODES, PlaybackError, PlaybackSessionError, PrepareToPlayOverlay, WATCH_ERROR_CODES, WATCH_ERROR_MESSAGES } from "@/lib/errors";
import getVisitorId from "@/lib/getVisitorId";
import maybeIOS from "@/lib/maybeIOS";
import { useApolloClient } from "@apollo/client";
import { useRouter } from "next/router";
import { useCallback, useEffect, useMemo, useReducer, useRef } from "react";
async function checkMobileDevice(deviceInfo) {
  var _deviceInfo$os;
  // 動画は全てDRMありのため、iOS11.2以上(fairplay対応OS)はwebで視聴可能
  // Androidはアプリキック
  // アダルトだったらエラー
  if (deviceInfo !== null && deviceInfo !== void 0 && deviceInfo.isAndroid) {
    throw new PrepareToPlayOverlay(null, PREPARE_TO_PLAY_OVERLAY_CODES["appkick"]);
  } else if (deviceInfo !== null && deviceInfo !== void 0 && deviceInfo.isIOS && (deviceInfo === null || deviceInfo === void 0 || (_deviceInfo$os = deviceInfo.os) === null || _deviceInfo$os === void 0 ? void 0 : _deviceInfo$os.version) < 11.2) {
    // TODO:AdultもVODも関係なくエラーを投げるため、仕様確認して名前とエラーコードを更新する
    throw new AdultNotViewableError();
  }
}
async function checkMember(user, meta) {
  if (rightsControlType[meta.rights_control_type] > rightsControlType.free && !user) {
    const code = (() => {
      switch (rightsControlType[meta.rights_control_type]) {
        case rightsControlType.member_only:
          return PREPARE_TO_PLAY_OVERLAY_CODES["needlogin"];
        case rightsControlType.entitled_only:
          return PREPARE_TO_PLAY_OVERLAY_CODES["needlogin_and_contract"];
      }
    })();
    throw new PrepareToPlayOverlay(null, code);
  }
}
async function checkAgeRestriction(user, meta) {
  if (user && user !== null && user !== void 0 && user.currentProfile) {
    var _user$currentProfile;
    const permitRating = userRating[user === null || user === void 0 || (_user$currentProfile = user.currentProfile) === null || _user$currentProfile === void 0 ? void 0 : _user$currentProfile.rating].permitRating;
    if (!permitRating.includes(meta.rating)) {
      throw new PrepareToPlayOverlay(null, PREPARE_TO_PLAY_OVERLAY_CODES["rating"]);
    }
  }
}
async function checkPlaybackError(playback) {
  if (playback !== null && playback !== void 0 && playback.error) {
    var _playback$error, _playback$error2;
    const code = playback === null || playback === void 0 || (_playback$error = playback.error) === null || _playback$error === void 0 ? void 0 : _playback$error.code;
    const message = playback === null || playback === void 0 || (_playback$error2 = playback.error) === null || _playback$error2 === void 0 ? void 0 : _playback$error2.message;
    throw new PlaybackError(message, code);
  }
}
const GEOLOCATION_ACCURACY_LIMIT = 5000;
const GEOLOCATION_TIMEOUT = 30000;
async function checkArea(user, meta, deviceInfo) {
  if (meta.area_control_flag) {
    if (window.navigator.geolocation) {
      var _user$currentProfile2;
      const areaCheck = new AreaCheckAPI(user === null || user === void 0 || (_user$currentProfile2 = user.currentProfile) === null || _user$currentProfile2 === void 0 ? void 0 : _user$currentProfile2.uuid);
      return new Promise((resolve, reject) => {
        const watchId = window.navigator.geolocation.watchPosition(async pos => {
          const {
            latitude,
            longitude,
            accuracy
          } = pos.coords;
          if (accuracy < GEOLOCATION_ACCURACY_LIMIT) {
            window.navigator.geolocation.clearWatch(watchId);
            clearTimeout(watchTimeoutId);
            const res = await areaCheck.check(latitude, longitude);
            const {
              ok,
              json
            } = res;
            if (json.result) {
              resolve();
            } else {
              let message;
              if (ok) {
                message = json.reason.message;
              } else {
                message = WATCH_ERROR_MESSAGES[WATCH_ERROR_CODES["episode_not_viewable"]];
              }
              reject(new GeoLocationError(message, json.reason.code));
            }
          }
        }, err => {
          window.navigator.geolocation.clearWatch(watchId);
          clearTimeout(watchTimeoutId);
          // PositionError.code
          // 1: PERMISSION_DENIED
          // 2: POSITION_UNAVAILABLE
          // 3: TIMEOUT
          const MESSAGES = (() => {
            if (deviceInfo !== null && deviceInfo !== void 0 && deviceInfo.isIOS) {
              return GEOLOCATION_ERROR_MESSAGES_IOS;
            }
            if (deviceInfo !== null && deviceInfo !== void 0 && deviceInfo.isAndroid) {
              return GEOLOCATION_ERROR_MESSAGES_ANDROID;
            }
            return GEOLOCATION_ERROR_MESSAGES;
          })();
          const code = err.code ? Number(err.code) : 99;
          const message = MESSAGES[code];
          reject(new GeoLocationError(message, code));
        }, {
          enableHighAccuracy: true,
          maximumAge: 0
        });
        const watchTimeoutId = setTimeout(() => {
          window.navigator.geolocation.clearWatch(watchId);
          // タイムアウトエラー
          // 位置情報の取得がタイムアウトしました。ご利用状況をご確認の上、しばらく経ってから電波の良い場所でご利用ください。
          const code = GEOLOCATION_ERROR_CODES["geolocation_timeout"];
          reject(new GeoLocationError(null, code));
        }, GEOLOCATION_TIMEOUT);
      });
    } else {
      const code = GEOLOCATION_ERROR_CODES["geolocation_unusable"];
      throw new GeoLocationError(null, code);
    }
  }
}
async function checkResume(resumePoint, playback) {
  if (resumePoint == null && playback.resumePoint > 0) {
    // ダイアログをだす
    throw new PrepareToPlayOverlay(null, PREPARE_TO_PLAY_OVERLAY_CODES["resume"]);
  }
}
async function checkLiveBefore(meta) {
  if (meta.is_live) {
    const now = dayjs();
    const encodeStart = dayjs(new Date(meta.encode_start_date));
    if (meta.encode_start_date && now.isBefore(encodeStart)) {
      throw new PrepareToPlayOverlay(null, PREPARE_TO_PLAY_OVERLAY_CODES["live_before"]);
    }
  }
}
async function checkMobileOS(deviceInfo, watchOnBrowserFlag) {
  if (!watchOnBrowserFlag && maybeIOS(deviceInfo)) {
    throw new PrepareToPlayOverlay(null, PREPARE_TO_PLAY_OVERLAY_CODES["suggest_app"]);
  }
}
async function checkUseEntitlement(confirmed, entitlements) {
  if (confirmed) return;
  // playback/authで権利が行使されるため、playbackAuthをコールする前に確認をださないといけない
  // 行使対象の権利にexercise_durationがあって、exercised_atがない場合は行使前
  const filterd = entitlements.find(entitlement => entitlement.exercise_duration && !entitlement.exercised_at);
  if (filterd) {
    throw new PrepareToPlayOverlay(null, PREPARE_TO_PLAY_OVERLAY_CODES["entitlement_confirm"]);
  }
}
async function checkDVRLive(meta, resumePoint) {
  if (meta.is_live && meta.start_over_flag) {
    const now = dayjs();
    const encodeStart = dayjs(new Date(meta.encode_start_date));
    const encodeEnd = dayjs(new Date(meta.encode_end_date));
    const isOnair = (() => {
      if (meta.encode_start_date && meta.encode_end_date) {
        return now.isBetween(encodeStart, encodeEnd, null, "[)");
      } else if (meta.encode_start_date && !meta.encode_end_date) {
        return now.isSameOrAfter(encodeStart);
      } else if (!meta.encode_start_date && meta.encode_end_date) {
        return now.isBefore(encodeEnd);
      }
    })();
    if (isOnair && resumePoint === null) {
      throw new PrepareToPlayOverlay(null, PREPARE_TO_PLAY_OVERLAY_CODES["dvr_live"]);
    }
  }
}
async function checkChat(rooms, resumePoint, meta) {
  if ((rooms === null || rooms === void 0 ? void 0 : rooms.length) > 0 && resumePoint == null) {
    const encodeEndDate = meta !== null && meta !== void 0 && meta.encode_end_date ? dayjs(meta === null || meta === void 0 ? void 0 : meta.encode_end_date) : null;
    if (encodeEndDate) {
      var _rooms$;
      const now = dayjs();
      const liveEndAfter = ((_rooms$ = rooms[0]) === null || _rooms$ === void 0 || (_rooms$ = _rooms$.customData) === null || _rooms$ === void 0 ? void 0 : _rooms$.liveEndAfter) || 0;
      const chatEndTime = encodeEndDate.add(liveEndAfter, "minute");
      if (chatEndTime.isAfter(now)) {
        // チャット終了時間が過ぎていなければダイアログをだす
        throw new PrepareToPlayOverlay(null, PREPARE_TO_PLAY_OVERLAY_CODES["chat_select"]);
      }
    }
  }
}

//metaにはepisode, entitlementにはhowtoplayが入る
export function usePrepareToPlay(meta, entitlements) {
  var _playbackAuth$current2, _playbackAuth$current9;
  const router = useRouter();
  const mediaSource = useRef();
  const playbackAuth = useRef();
  const resetFlag = useRef(false);
  const initialState = useMemo(() => {
    return {
      loading: true,
      prepared: false,
      // prepare処理が完了しているか
      canplay: false,
      // mediaを取得して動画再生が可能か
      overlay: false,
      // オーバーレイを出す必要があるか
      error: null,
      // エラーオブジェクト
      reason: "",
      // エラー、オーバーレイの事由
      resumePoint: null,
      // 再生を開始する秒数
      entitlementConfirmed: false,
      // 権利の行使を確認したか
      watchOnBrowserFlag: false // 「ブラウザで見る」を選択したかどうか
    };
  }, []);
  const reducer = useCallback((state, action) => {
    switch (action.type) {
      case "update":
        return _objectSpread(_objectSpread({}, state), action.payload);
      case "overlay":
        return _objectSpread(_objectSpread({}, state), {}, {
          canplay: false,
          loading: false,
          prepared: false,
          overlay: true
        }, action.payload);
      case "canplay":
        return _objectSpread(_objectSpread({}, state), {}, {
          loading: false,
          canplay: true,
          overlay: false,
          error: null,
          reason: ""
        });
      case "resetAndUpdate":
        return _objectSpread(_objectSpread(_objectSpread({}, initialState), {}, {
          loading: false,
          watchOnBrowserFlag: state.watchOnBrowserFlag
        }, action.payload), {}, {
          rooms: state.rooms
        });
      case "reset":
        return initialState;
      default:
        return state;
    }
  }, [initialState]);
  const {
    0: state,
    1: dispatch
  } = useReducer(reducer, initialState);
  const client = useApolloClient();
  const {
    data
  } = useAuthUserQuery();
  const user = data === null || data === void 0 ? void 0 : data.user;
  const {
    data: {
      deviceInfo
    } = {}
  } = useDeviceInfoQuery({
    fetchPolicy: "cache-first"
  });
  //分割代入
  const [loadPlaybackSessionOpen, {
    error: sessionError,
    data: sessionData
  }] = usePlaybackSessionOpenMutation();
  const [sessionCloseMutation] = usePlaybackSessionCloseMutation();
  const [getRoomsLazyQuery] = useChatGetRoomsLazyQuery({
    variables: {
      metaId: Number(meta.id)
    }
  });
  const getRooms = useCallback(async () => {
    if ((state === null || state === void 0 ? void 0 : state.rooms) == null) {
      var _result$getRooms, _result$getRooms2;
      const {
        data: result
      } = await getRoomsLazyQuery();
      update({
        rooms: result === null || result === void 0 || (_result$getRooms = result.getRooms) === null || _result$getRooms === void 0 ? void 0 : _result$getRooms.rooms
      });
      return result === null || result === void 0 || (_result$getRooms2 = result.getRooms) === null || _result$getRooms2 === void 0 ? void 0 : _result$getRooms2.rooms;
    } else {
      return state.rooms;
    }
  }, [state.rooms]);
  const sessionClose = useCallback(async () => {
    var _playbackAuth$current;
    if (playbackAuth.current && (_playbackAuth$current = playbackAuth.current) !== null && _playbackAuth$current !== void 0 && _playbackAuth$current.playback_session_id) {
      await sessionCloseMutation({
        variables: {
          sessionId: playbackAuth.current.playback_session_id
        },
        onError: e => {
          console.error("sessionCloseMutation error", e);
        }
      });
      playbackAuth.current = null;
    }
  }, [(_playbackAuth$current2 = playbackAuth.current) === null || _playbackAuth$current2 === void 0 ? void 0 : _playbackAuth$current2.playback_session_id]);
  const sessionCloseById = useCallback(async playbackSessionId => {
    if (playbackSessionId) {
      await sessionCloseMutation({
        variables: {
          sessionId: playbackSessionId
        },
        onError: e => {
          console.error("sessionCloseMutation error", e);
        }
      });
    }
  }, []);
  const standWatchOnBrowserFlag = useCallback(() => {
    dispatch({
      type: "resetAndUpdate",
      payload: {
        loading: false,
        watchOnBrowserFlag: true
      }
    });
  }, []);

  // resumePointを更新
  const updateResumePoint = useCallback(resumePoint => {
    dispatch({
      type: "resetAndUpdate",
      payload: {
        loading: false,
        resumePoint
      }
    });
  }, []);

  // 権利行使確認
  const entitlementConfirm = useCallback(() => {
    dispatch({
      type: "resetAndUpdate",
      payload: {
        loading: false,
        entitlementConfirmed: true
      }
    });
  }, []);

  // 処理の中断
  const prepareSuspend = useCallback(() => {
    dispatch({
      type: "resetAndUpdate",
      payload: {
        loading: false,
        prepared: true
      }
    });
  }, []);

  // 再生開始の再確認
  const prepareReconfirm = useCallback(() => {
    dispatch({
      type: "update",
      payload: {
        loading: false,
        prepared: true,
        reason: "entitlement_reconfirm",
        overlay: false,
        canplay: true
      }
    });
  }, []);

  // プレイヤー再読み込み
  const resetAndUpdate = useCallback(async () => {
    console.log("resetAndUpdate start");
    // resetFlag.current = true;
    // mediaSource.current = null;
    playbackAuth.current = null;
    dispatch({
      type: "resetAndUpdate",
      payload: _objectSpread(_objectSpread({}, initialState), {}, {
        rooms: state.rooms
      })
    });
    //resetFlag.current = false;
    console.log("resetAndUpdate end");
  }, []);

  // 連続再生対応でreset関数を改修
  const reset = useCallback(async () => {
    var _playbackAuth$current3;
    // playbackSessionがあればクローズする
    resetFlag.current = true;
    const playbackSessionId = playbackAuth === null || playbackAuth === void 0 || (_playbackAuth$current3 = playbackAuth.current) === null || _playbackAuth$current3 === void 0 ? void 0 : _playbackAuth$current3.playback_session_id;
    mediaSource.current = null;
    playbackAuth.current = null;
    dispatch({
      type: "reset"
    });
    if (!!playbackSessionId) {
      await sessionCloseById(playbackSessionId);
    }
    resetFlag.current = false;
  }, []);

  // 再生可能
  const canplay = useCallback(() => {
    dispatch({
      type: "canplay"
    });
  }, []);

  // state のupdate
  const update = useCallback(payload => {
    dispatch({
      type: "update",
      payload
    });
  }, []);

  // オーバーレイの表示
  const displayOverlay = useCallback(payload => {
    dispatch({
      type: "overlay",
      payload
    });
  }, []);
  // 視聴許可判定
  const execPlaybackAuth = useCallback(async () => {
    var _playbackAuth$current4;
    const vuid = await getVisitorId();
    // playback_session_idを渡すと同一端末で同時視聴制限がかかってしまうのを防げるっぽい
    // どういうときにそういう状況になるか考えて値の保存の仕方を考えた方が良さそう
    // paraviはcookieに保存していたけど、cookieはあまり使いたくない
    const psId = (_playbackAuth$current4 = playbackAuth.current) === null || _playbackAuth$current4 === void 0 ? void 0 : _playbackAuth$current4.playback_session_id;
    playbackAuth.current = null;
    const {
      data,
      error
    } = await client.query({
      query: GET_PLAYBACK,
      fetchPolicy: "network-only",
      variables: {
        id: parseInt(meta.id, 10),
        vuid,
        sessionId: psId,
        fl: user && user.test_flg ? 1 : 0
      }
    });
    if (data) {
      playbackAuth.current = data.playbackAuth;
    } else if (error) {
      displayOverlay({
        reason: "error",
        error: authError
      });
    }
  }, [meta, user]);
  const prepareToPlay = useCallback(async function (argDeviceInfo) {
    let force = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
    try {
      let useDeviceInfo = deviceInfo !== null && deviceInfo !== void 0 ? deviceInfo : argDeviceInfo;
      if (!force && (state.prepared || !useDeviceInfo || resetFlag.current)) return;
      let setRoomsData = null;
      // ユーザー確認
      await checkMember(user, meta);
      // 視聴年齢制限
      await checkAgeRestriction(user, meta);
      // チャット情報取得 普通のuseQueryではstateへの反映が遅れてcheckChat時点でチャットなしと判定されるので、ここでQuery返り値の取得とstate更新キューセットの2つを行なっている
      if (user && !useDeviceInfo.isAndroid) {
        // ログイン時&Android以外のみ取得
        setRoomsData = await getRooms();
      }
      if (!playbackAuth.current) {
        // - tvod 権利行使確認
        await checkUseEntitlement(state.entitlementConfirmed, entitlements);
        // exec playback auth
        await execPlaybackAuth();
      }
      if (playbackAuth.current) {
        // 権利があるかなど、playback error
        await checkPlaybackError(playbackAuth.current);
        // - モバイルデバイス（Appkick）
        await checkMobileDevice(useDeviceInfo);
        // - エリアチェックが必要であれば実行
        await checkArea(user, meta, useDeviceInfo);
        // // - ブラウザで視聴可能なモバイルOSかどうか
        await checkMobileOS(useDeviceInfo, state.watchOnBrowserFlag);
        // - ライブが始まっているか
        await checkLiveBefore(meta);
        // ライブ再生位置選択
        await checkDVRLive(meta, state.resumePoint);
        // - レジュームの確認
        await checkResume(state.resumePoint, playbackAuth.current);
        if (user) {
          // ログイン時のみ取得
          // チャット有無の確認 resumePointはレジュームかチャット有無選択を押下すればnullではなくなるので、再レンダ時は処理させないために使っている
          await checkChat(setRoomsData, state.resumePoint, meta);
        }
        update({
          prepared: true
        });
      }
    } catch (e) {
      //上のcheckMobileDeviceとかの返り値としてthrowで投げられるエラーオブジェクトはここでcatchする
      //cathchしたe.reasonに応じたdisplayOverlayを実行する
      if (e instanceof PrepareToPlayOverlay) {
        displayOverlay({
          reason: e.reason
        });
      } else {
        sessionClose();
        if (e instanceof PlaybackError) {
          if (deviceInfo !== null && deviceInfo !== void 0 && deviceInfo.isAndroid && e.reason === "not_delivery_started") {
            displayOverlay({
              reason: "appkick"
            });
          } else {
            displayOverlay({
              reason: "playback_error",
              error: e
            });
          }
        } else if (e instanceof GeoLocationError) {
          displayOverlay({
            reason: "geolocation",
            error: e
          });
        } else {
          console.log(e);
          displayOverlay({
            reason: "error",
            error: e
          });
        }
      }
    }
  }, [user, meta, entitlements, state, playbackAuth.current, deviceInfo]);

  // load playbackSession
  useEffect(() => {
    var _playbackAuth$current5;
    if (state.prepared && playbackAuth.current && !((_playbackAuth$current5 = playbackAuth.current) !== null && _playbackAuth$current5 !== void 0 && _playbackAuth$current5.error) && !mediaSource.current && !sessionError) {
      var _playbackAuth$current6, _playbackAuth$current7;
      loadPlaybackSessionOpen({
        variables: {
          videoId: (_playbackAuth$current6 = playbackAuth.current) === null || _playbackAuth$current6 === void 0 || (_playbackAuth$current6 = _playbackAuth$current6.media) === null || _playbackAuth$current6 === void 0 ? void 0 : _playbackAuth$current6.ovp_video_id,
          sessionId: (_playbackAuth$current7 = playbackAuth.current) === null || _playbackAuth$current7 === void 0 ? void 0 : _playbackAuth$current7.playback_session_id
        }
      });
    }
  }, [state.prepared, playbackAuth.current, mediaSource.current, sessionError]);

  // set playback media
  useEffect(() => {
    if (sessionData) {
      var _sessionData$playback;
      if ((_sessionData$playback = sessionData.playbackSessionOpen) !== null && _sessionData$playback !== void 0 && _sessionData$playback.error) {
        displayOverlay({
          reason: "error",
          error: new PlaybackSessionError(sessionData.playbackSessionOpen.error.message, sessionData.playbackSessionOpen.error.code)
        });
      } else {
        mediaSource.current = sessionData.playbackSessionOpen.media;
        canplay();
      }
    } else if (sessionError) {
      displayOverlay({
        reason: "error",
        error: sessionError
      });
    }
  }, [sessionError, sessionData]);

  // ページ離脱時にsessionClose
  useEffect(() => {
    var _playbackAuth$current8;
    // 依存配列に変化があるとクリーンナップ関数が実行されるため、それを防止する
    if (!deviceInfo || !((_playbackAuth$current8 = playbackAuth.current) !== null && _playbackAuth$current8 !== void 0 && _playbackAuth$current8.playback_session_id)) {
      return;
    }
    let closed = false;
    const close = () => {
      if (closed) {
        return;
      }
      closed = true;
      sessionClose();
    };
    //iOSとMacのSafariの場合
    const isIOS = maybeIOS(deviceInfo);
    if (isIOS || deviceInfo !== null && deviceInfo !== void 0 && deviceInfo.isMac && deviceInfo !== null && deviceInfo !== void 0 && deviceInfo.isSafari) {
      window.addEventListener("pagehide", close);
      router.beforePopState(_ref => {
        let {
          url,
          as,
          options
        } = _ref;
        return close;
      });
      return () => {
        window.removeEventListener("pagehide", close);
        router.beforePopState(() => true);
        if (!closed) {
          close();
        }
      };
    }
    //それ以外の場合
    else {
      window.addEventListener("beforeunload", close);
      return () => {
        window.removeEventListener("beforeunload", close);
        if (!closed) {
          close();
        }
      };
    }
  }, [deviceInfo, (_playbackAuth$current9 = playbackAuth.current) === null || _playbackAuth$current9 === void 0 ? void 0 : _playbackAuth$current9.playback_session_id]);
  return [prepareToPlay, _objectSpread(_objectSpread({}, state), {}, {
    playbackAuth: playbackAuth.current,
    mediaSource: mediaSource.current,
    updateResumePoint,
    entitlementConfirm,
    displayOverlay,
    reset,
    sessionClose,
    prepareSuspend,
    prepareReconfirm,
    standWatchOnBrowserFlag,
    resetAndUpdate
  })];
}